import { ClientOnly, createFileRoute, Link, redirect, useNavigate } from "@tanstack/react-router";
import { useCallback } from "react";
import { ArrowLeftIcon } from "lucide-react";

import {
  ComposedBlocks,
  publicCommunityQuotes,
  type BlockRenderContext,
} from "@/web/components/styled/ComposedBlocks";
import { Button, buttonVariants } from "@/web/components/primitives/button";
import { ComposedTestimonials } from "@/web/components/styled/ComposedTestimonials";
import { FullPageLoader } from "@/web/components/styled/FullPageLoader";
import { LivePledgeLayer } from "@/web/components/styled/LivePledgeLayer";
import { parseBlocksPayload, type PageBlock } from "@/web/lib/composerBlocks";
import { safeEmbedUrl } from "@/web/lib/composerEmbeds";
import { env } from "@/web/lib/env";
import { ttwResultToContent } from "@/web/routes/ttw/-page/content";
import { InvestmentSidebar } from "@/web/routes/ttw/-page/InvestmentSidebar";
import { TtwPage } from "@/web/routes/ttw/-page/TtwPage";
import { usePublicOffering } from "@/web/routes/ttw/-page/usePublicOffering";
import { isAuthError, isNotFoundError } from "@/web/lib/spacetimedbErrors";
import { cn } from "@/web/lib/styling";
import { useProcedureQueryLazy } from "@/web/lib/tanstack";
import {
  PasswordGate,
  cardQueryKey,
  parseCardId,
  prefetchShareableCard,
  useShareableCardGate,
} from "@/web/routes/-shareableCardGate/shareableCardGate";

import { procedures } from "@tycho/web-bindings";
import type {
  GetShareableBusinessCardResult,
  GetTtwPageResult,
} from "@tycho/web-bindings/types/procedures";
import type { Uuid } from "spacetimedb";

import { TtwCard, type TtwCardPresentation } from "./-TtwCard";

/**
 * `?invest=true` — the post-sign-in deep link for the invest CTA.
 *
 * A SEARCH PARAM, not React state, because auth completes with a full page load
 * (`window.location.assign` in `useAuthnDialogFlow`), which discards everything
 * in memory. The param survives that round trip and is stripped once consumed
 * (see `handleAutoOpenInvest`) so a later refresh doesn't reopen checkout.
 */
type TtwSearch = {
  invest?: boolean;
};

function validateTtwSearch(raw: Record<string, unknown>): TtwSearch {
  return {
    invest: raw.invest === true || raw.invest === "true" ? true : undefined,
  };
}

export const Route = createFileRoute("/ttw/$slug")({
  ssr: true,
  validateSearch: validateTtwSearch,
  loader: async ({ params, context }) => {
    if (params.slug === env.VITE_COMMUNITY_THEATER_CARD_ID) {
      throw redirect({ to: "/community-theater", replace: true });
    }
    if (parseCardId(params.slug) === undefined) return;
    await prefetchShareableCard(context.queryClient, params.slug);
  },
  head: ({ match }) => {
    const card = match.context.queryClient.getQueryData<GetShareableBusinessCardResult>(
      cardQueryKey(match.params.slug, ""),
    );
    if (!card?.brandFriendlyName) return { meta: [{ title: "WITH.CO" }] };
    const title = card.brandFriendlyName;
    const description =
      card.brandBusinessType && card.brandBusinessType.length > 0
        ? `${title} — ${card.brandBusinessType}. Shared via WITH.CO.`
        : `View ${title}'s business card on WITH.CO.`;
    const hero = card.brandHeroCdnPath
      ? `${env.VITE_WITHCO_IMAGE_CDN_BASE}/${card.brandHeroCdnPath}`
      : undefined;
    const meta: Array<{ title?: string; name?: string; property?: string; content?: string }> = [
      { title: `${title} — WITH.CO` },
      { name: "description", content: description },
      { property: "og:title", content: title },
      { property: "og:description", content: description },
      { property: "og:type", content: "website" },
      { property: "og:site_name", content: "WITH.CO" },
      { property: "og:locale", content: "en_US" },
      { name: "twitter:card", content: hero ? "summary_large_image" : "summary" },
      { name: "twitter:title", content: title },
      { name: "twitter:description", content: description },
    ];
    if (hero) {
      meta.push(
        { property: "og:image", content: hero },
        { property: "og:image:alt", content: `${title} hero image` },
        { name: "twitter:image", content: hero },
        { name: "twitter:image:alt", content: `${title} hero image` },
      );
    }
    return { meta };
  },
  component: RouteComponent,
});

// ===== route component =====

function RouteComponent() {
  const { slug } = Route.useParams();
  const { cardId, accessCode, setAccessCode, cardQuery, queryKey } = useShareableCardGate(slug);

  if (cardId === undefined || isNotFoundError(cardQuery.error)) {
    return (
      <div className="mx-auto flex min-h-screen max-w-2xl flex-col items-center justify-center gap-4 px-4 text-center">
        <h1 className="withus-strong text-2xl">Card not found</h1>
        <p>Double-check the link and try again.</p>
      </div>
    );
  }
  if (isAuthError(cardQuery.error)) {
    return <PasswordGate onSubmit={setAccessCode} hasError={accessCode.length > 0} />;
  }
  if (cardQuery.isError) {
    return (
      <div className="mx-auto flex min-h-screen max-w-2xl flex-col items-center justify-center gap-4 px-4 text-center">
        <h1 className="withus-strong text-2xl">Something went wrong</h1>
        <p>Please try again later.</p>
      </div>
    );
  }
  if (cardQuery.isLoading || !cardQuery.data) {
    return <FullPageLoader />;
  }

  const card = cardQuery.data;

  return (
    <ComposedPageGate cardId={cardId} card={card} accessCode={accessCode} refetchKey={queryKey} />
  );
}

/**
 * Decides what this card renders, in precedence order:
 *   1. a structured TTW page — `getTtwPage` (the designed investor page);
 *   2. an LLM-composed page — `getComposerPage` (dormant; no prod data);
 *   3. the plain pledge `<TtwCard>`.
 * All reads run client-side (post-hydration) and pass the gate's access code so
 * they're subject to the same password gate as the card; while they're in flight
 * we hold a loader to avoid a content flash. The composed page overlays the
 * shared `<LivePledgeLayer>` so pledging is never lost; the TTW page carries its
 * own sidebar pledge CTA instead.
 */
function ComposedPageGate({
  cardId,
  card,
  accessCode,
  refetchKey,
}: {
  cardId: Uuid;
  card: GetShareableBusinessCardResult;
  accessCode: string;
  refetchKey: readonly unknown[];
}) {
  // Both reads re-validate the card gate server-side (can't be read by UUID
  // alone). The TTW page takes precedence over the LLM composer page.
  const ttwPageQuery = useProcedureQueryLazy(procedures.getTtwPage, { cardId, accessCode });
  const composedPageQuery = useProcedureQueryLazy(procedures.getComposerPage, {
    cardId,
    accessCode,
  });

  // Hold on a loader until BOTH lookups settle. Committing to a branch while
  // either is still `isLoading` (which also covers "connection not ready yet" —
  // `useProcedure` queues the call) would paint the pledge card first and then
  // swap once a query resolves: the content flash. `useProcedure` queues rather
  // than erroring, so this never hangs.
  if (ttwPageQuery.isLoading || composedPageQuery.isLoading) {
    return <FullPageLoader />;
  }

  // A REJECTED read is not "this card has no page". Falling through on error
  // would silently serve the plain pledge card in place of the owner's real
  // investor page — the visitor sees a downgraded page and no indication that
  // anything failed, and the owner has no way to tell it's happening. Surface
  // it and offer a retry instead.
  //
  // The access-code case is already handled upstream by the gate, which catches
  // `[NOT_AUTHORIZED]` and renders `<PasswordGate />`; anything reaching here is
  // a genuine fault (dropped connection, server error).
  if (ttwPageQuery.isError || composedPageQuery.isError) {
    return (
      <PageLoadError
        onRetry={() => {
          void ttwPageQuery.refetch();
          void composedPageQuery.refetch();
        }}
      />
    );
  }

  // 1. Structured TTW page. `hasPage` is authoritative — the server sets it
  // rather than the client sniffing a sentinel field.
  const ttwPage = ttwPageQuery.data;
  if (ttwPage?.hasPage) {
    return (
      // The property address is NOT passed from the card here: it resolves
      // first-link-wins over the entity's targets, which is a different
      // building than this page's whenever the entity targets more than one.
      // `getTtwPage` returns the page's own property address instead.
      <PublicTtwPage cardId={cardId} accessCode={accessCode} page={ttwPage} />
    );
  }

  // 2. LLM-composed page (dormant fallback).
  const composedBody = composedPageQuery.data?.body;
  const blocks = composedBody ? parseBlocksPayload(composedBody) : [];
  if (blocks.length > 0) {
    return (
      <ComposedTtwPage
        cardId={cardId}
        accessCode={accessCode}
        blocks={blocks}
        businessName={card.brandFriendlyName ?? "this business"}
        backerCount={card.expressionsOfInterestCount}
      />
    );
  }

  const presentation: TtwCardPresentation = {
    brandFriendlyName: card.brandFriendlyName,
    brandHeroCdnPath: card.brandHeroCdnPath,
    brandColorPrimary: card.brandColorPrimary,
    brandBusinessType: card.brandBusinessType,
    brandBusinessAddress: card.brandBusinessAddress,
    targetedPropertyAddress: card.targetedPropertyAddress,
  };

  return (
    <div className="relative flex min-h-screen w-full items-center justify-center bg-background px-4 py-8">
      <FloatingCardBackButton />
      <TtwCard
        presentation={presentation}
        card={card}
        cardId={cardId}
        accessCode={accessCode}
        refetchKey={refetchKey}
      />
    </div>
  );
}

/**
 * "← Back to browse" for the floating pledge-card fallback. This page exists
 * only because a shared `/ttw/<cardId>` link still works before the owner has
 * authored a real TTW page — the back button is the visitor's way out.
 */
function FloatingCardBackButton() {
  return (
    <Link
      to="/browse"
      className={cn(buttonVariants({ variant: "outline", size: "sm" }), "absolute top-4 left-4")}
    >
      <ArrowLeftIcon className="size-4" aria-hidden />
      Back to browse
    </Link>
  );
}

/**
 * Shown when a page read fails outright. Deliberately distinct from the "no
 * page" path: a card with no page legitimately falls through to the pledge
 * card, but a failed read must not be mistaken for one.
 */
function PageLoadError({ onRetry }: { onRetry: () => void }) {
  return (
    <div className="mx-auto flex min-h-screen max-w-2xl flex-col items-center justify-center gap-4 px-4 text-center">
      <h1 className="withus-strong text-2xl">Something went wrong</h1>
      <p>We couldn't load this page. Please try again.</p>
      <Button color="blue" size="md" onClick={onRetry} className="rounded-none font-sans">
        Retry
      </Button>
    </div>
  );
}

/**
 * The public structured TTW page: the shared `<TtwPage>` renderer plus a sidebar
 * wired to live backer data.
 *
 * The backer feed is `<ClientOnly>` because its "10m ago" stamps are computed
 * against `Date.now()` — rendering them on the server would guarantee a
 * hydration mismatch. The page body around it still server-renders.
 */
function PublicTtwPage({
  cardId,
  accessCode,
  page,
}: {
  cardId: Uuid;
  accessCode: string;
  page: GetTtwPageResult;
}) {
  // The card id drives the hero's share button. Passed as an id rather than a
  // URL so the button renders identically on the SSR and hydration passes —
  // see `HeroShareButton`.
  const baseContent = ttwResultToContent(page, cardId);
  const backersQuery = useProcedureQueryLazy(procedures.getTtwPageBackers, {
    cardId,
    accessCode,
  });

  // The page's STORED stage is always `planning` — nothing advances that column
  // when an offering is promoted, so the hero badge read "Planning" on a page
  // that was actively taking money. The live raise is the authority, exactly as
  // it is for the browse card (`hasOpenOffering` → `fundraising`), and this is
  // the SAME read the sidebar's CTA gates on, so badge and button always agree.
  const { offering } = usePublicOffering(cardId);
  const content = offering ? { ...baseContent, stage: "fundraising" as const } : baseContent;

  // `?invest=true` survives the sign-in reload; strip it once the sidebar has
  // acted on it so a refresh (or a shared URL) doesn't reopen checkout.
  const search = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  const handleAutoOpenInvest = useCallback(() => {
    if (!search.invest) return;
    void navigate({
      search: (prev) => ({ ...prev, invest: undefined }),
      replace: true,
    });
  }, [navigate, search.invest]);

  // Stamped before the auth dialog opens so the post-auth redirect — which every
  // branch of `useAuthnDialogFlow` builds from `window.location` — carries the
  // deep link back here.
  //
  // `history.replaceState` rather than the router's `navigate`: `navigate`
  // returns a promise and does not guarantee the address bar has changed by the
  // time the dialog mounts in the same commit, and the magic-link branch
  // captures its verification URL on that mount. A direct replaceState is
  // synchronous, so the param is always present before anything reads it. The
  // router picks the change up on its next location sync; nothing here depends
  // on it re-rendering.
  const handleRequestAuthRedirect = useCallback(() => {
    if (typeof window === "undefined") return;
    const url = new URL(window.location.href);
    url.searchParams.set("invest", "true");
    window.history.replaceState(window.history.state, "", url);
  }, []);

  return (
    <TtwPage
      content={content}
      showHeader
      ownerBrand={page.ownerBrand}
      sidebar={
        <ClientOnly>
          <InvestmentSidebar
            cardId={cardId}
            businessName={content.businessName}
            sharePrice={content.sharePrice}
            targetReturnRange={content.targetReturnRange}
            showTotalReserved={content.showTotalReserved}
            showAppreciationShare={content.showAppreciationShare}
            appreciationSharePercent={content.appreciationSharePercent}
            backers={backersQuery.data}
            onPledged={() => backersQuery.refetch().then(() => undefined)}
            autoOpenInvest={search.invest === true}
            onAutoOpenInvestHandled={handleAutoOpenInvest}
            onRequestAuthRedirect={handleRequestAuthRedirect}
          />
        </ClientOnly>
      }
    />
  );
}

/**
 * Extract every `{{embed:<url>}}` token from the saved HTML, keeping only
 * allowlisted (YouTube/Vimeo) URLs, and return the HTML with each embed token
 * (and the `<iframe>` wrapping it, if present) removed. Embeds can't run inside
 * the `sandbox=""` composed iframe, so they're rendered separately as trusted
 * React iframes; stripping the dead token here avoids a broken/blank frame.
 */
function extractEmbeds(html: string): { html: string; embedUrls: string[] } {
  const embedUrls: string[] = [];
  // Remove any <iframe ... src="{{embed:URL}}" ...></iframe> around a token, then
  // any bare leftover tokens, collecting the safe URLs as we go.
  const withoutIframes = html.replace(
    /<iframe[^>]*src=["']\{\{embed:([^}]+)\}\}["'][^>]*>\s*<\/iframe>/gi,
    (_match, url: string) => {
      const safe = safeEmbedUrl(url);
      if (safe) embedUrls.push(safe);
      return "";
    },
  );
  const cleaned = withoutIframes.replace(/\{\{embed:([^}]+)\}\}/g, (_match, url: string) => {
    const safe = safeEmbedUrl(url);
    if (safe && !embedUrls.includes(safe)) embedUrls.push(safe);
    return "";
  });
  return { html: cleaned, embedUrls };
}

/**
 * Public composed page: the ordered block array rendered as interleaved HTML
 * sections + live components (see `ComposedBlocks`), with the shared live pledge
 * layer overlaid at the bottom. No funding-goal bar yet (no pledged-$ total is
 * exposed for arbitrary cards — see LLM_TTW_COMPOSER.md); shows backer count +
 * Pledge/Share.
 *
 * Each `html` block is the owner's own composed markup, sanitized at Save time
 * and rendered inline (images/`<video controls>` load from their CDN URLs).
 * Pasted YouTube/Vimeo embeds are extracted per block and rendered as
 * allowlisted React iframes beside the block, not as raw markup.
 *
 * `communityQuotes` is a placeable live block (see `ComposedBlocks`). When the
 * saved page contains one, it renders inline at that position; when it doesn't
 * (older pages, or the model never placed one), we still append the quotes
 * section below the page — a page should never silently lose its community
 * voices. (Deliberate fallback per LLM_STRUCTURED_TTW_PAGE.md Step 2c.)
 */
function ComposedTtwPage({
  cardId,
  accessCode,
  blocks,
  businessName,
  backerCount,
}: {
  cardId: Uuid;
  accessCode: string;
  blocks: PageBlock[];
  businessName: string;
  backerCount: number;
}) {
  const ctx: BlockRenderContext = {
    // Public HTML is already CDN-rewritten + sanitized (Save-time); pull out
    // `{{embed:URL}}` tokens so they render as React iframes, not raw markup.
    prepareHtml: (rawHtml) => extractEmbeds(rawHtml),
    renderCommunityQuotes: () => (
      <ClientOnly>{publicCommunityQuotes(cardId, accessCode)}</ClientOnly>
    ),
  };
  const hasQuotesBlock = blocks.some((block) => block.type === "communityQuotes");

  return (
    <div className="flex min-h-dvh flex-col bg-background">
      <div className="flex flex-1 flex-col">
        <ComposedBlocks blocks={blocks} ctx={ctx} />
      </div>
      {/* Fallback community-quotes section when the page has no communityQuotes
          block, so a page never loses its community voices. Rendered from real
          consented pledges (or placeholders) OUTSIDE the sandboxed iframes. */}
      {!hasQuotesBlock ? (
        <ClientOnly>
          <ComposedTestimonials cardId={cardId} accessCode={accessCode} />
        </ClientOnly>
      ) : null}
      <ClientOnly>
        <div className="sticky bottom-0 z-30">
          <LivePledgeLayer cardId={cardId} businessName={businessName} backerCount={backerCount} />
        </div>
      </ClientOnly>
    </div>
  );
}
